import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { generateSportProfessionalMetaDescription } from "@/utils/metaDescriptions";
import { generateSportProfessionalTitle } from "@/utils/seoTitles";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SpiderwebLink } from "@/components/interlinking/SpiderwebLink";
import { RelatedInterlinks } from "@/components/interlinking/RelatedInterlinks";
import { Button } from "@/components/ui/button";
import { Trophy, Mail, Phone, MapPin, Star, Award, Users, Heart, ArrowLeft, CheckCircle2 } from "lucide-react";
import { VoteButton } from "@/components/voting/VoteButton";
import { UnirseATribuButton } from "@/components/shared/UnirseATribuButton";
import { buildPostalAddressStrict } from "@/utils/structuredData/postalAddress";

interface SportProfile {
  id: string;
  category: string;
  country: string;
  ranking: number;
  full_name: string;
  slug: string;
  bio: string | null;
  profile_image: string | null;
  email: string;
  phone: string | null;
  location_city: string | null;
  location_country: string;
  specialty: string[] | null;
  certifications: string[] | null;
  achievements: string[] | null;
  years_experience: number | null;
  clients_trained: number | null;
  hourly_rate: number | null;
  sport_background: string | null;
}

const SportProfessionalDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [profile, setProfile] = useState<SportProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [slug]);

  const fetchProfile = async () => {
    if (!slug) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("sport_category_profiles")
      .select("*")
      .eq("slug", slug)
      .eq("is_active", true)
      .single();

    if (!error && data) {
      setProfile(data);
    }
    setLoading(false);
  };

  const getMedalColor = (ranking: number) => {
    switch (ranking) {
      case 1: return "text-yellow-500";
      case 2: return "text-gray-400";
      case 3: return "text-amber-600";
      default: return "text-primary";
    }
  };

  const categoryName = profile?.category
    ?.split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "";

  if (loading) {
    return (
      <>
        <Navigation />
        <main className="pt-32 lg:pt-40 pb-16 min-h-screen">
          <div className="container mx-auto px-4">
            <div className="text-center py-12">
              <p className="text-muted-foreground">Cargando perfil...</p>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!profile) {
    // Self-canonical + noindex: previously this branch rendered no <SEO>, so the
    // static homepage canonical leaked and GSC reported these URLs as
    // "alternate page with proper canonical".
    return (
      <>
        <SEO
          title="Perfil no disponible"
          description="Este perfil profesional ya no está publicado en Ekselencia."
          noindex
        />
        <Navigation />
        <main className="pt-32 lg:pt-40 pb-16 min-h-screen">
          <div className="container mx-auto px-4">
            <div className="text-center py-12">
              <p className="text-muted-foreground">Perfil no encontrado</p>
              <Button asChild className="mt-4">
                <Link to="/los-mejores">Volver a Los Mejores</Link>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Generate structured data for Person schema
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: profile.full_name,
    jobTitle: `Profesional de ${categoryName}`,
    description: profile.bio || `Profesional deportivo especializado en ${categoryName}`,
    image: profile.profile_image || '/default-listing-fallback.png',
    email: profile.email,
    telephone: profile.phone || undefined,
    ...(buildPostalAddressStrict({
      streetAddress: null,
      addressLocality: profile.location_city,
      postalCode: null,
      addressRegion: null,
      addressCountry: profile.location_country || "ES",
    })
      ? {
          address: buildPostalAddressStrict({
            streetAddress: null,
            addressLocality: profile.location_city,
            postalCode: null,
            addressRegion: null,
            addressCountry: profile.location_country || "ES",
          }),
        }
      : {}),
    knowsAbout: profile.specialty || [],
    hasCredential: profile.certifications || [],
    award: profile.achievements || [],
    worksFor: {
      "@type": "Organization",
      name: "Ekselencia",
    },
    sameAs: [],
  };

  // Generate unique meta description
  const metaDescription = generateSportProfessionalMetaDescription({
    full_name: profile.full_name,
    category: profile.category,
    ranking: profile.ranking,
    location_country: profile.location_country,
    location_city: profile.location_city || undefined,
    bio: profile.bio || undefined,
    years_experience: profile.years_experience || undefined,
    clients_trained: profile.clients_trained || undefined,
    achievements: profile.achievements || undefined,
    slug: profile.slug,
  });

  return (
    <>
      <SEO 
        title={generateSportProfessionalTitle({
          name: profile.full_name,
          specialty: categoryName,
          city: profile.location_city || undefined,
          country: profile.location_country,
          slug: profile.slug,
        })}
        description={metaDescription}
        keywords={[
          profile.full_name,
          categoryName,
          profile.location_country,
          profile.location_city || '',
          ...profile.specialty || [],
          'profesional deportivo',
          'ranking',
        ]}
        ogImage={profile.profile_image || '/default-listing-fallback.png'}
        ogType="profile"
        structuredData={structuredData}
      />
      <Navigation />
      
      <main className="pt-32 lg:pt-40 pb-16 min-h-screen">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Back Button */}
          <Button variant="ghost" asChild className="mb-6">
            <Link to={`/los-mejores/${profile.category}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a {categoryName}
            </Link>
          </Button>

          {/* Header Card */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-[300px,1fr] gap-8">
                {/* Profile Image */}
                <div className="space-y-4">
                  <figure className="aspect-square rounded-lg bg-secondary/20 overflow-hidden">
                    {profile.profile_image ? (
                      <img 
                        src={profile.profile_image} 
                        alt={`${profile.full_name} - Profesional de ${categoryName} #${profile.ranking} en ${profile.location_country}`}
                        className="w-full h-full object-cover"
                        loading="eager"
                        width="300"
                        height="300"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.src = "/default-listing-fallback.png";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" role="img" aria-label="Imagen de perfil no disponible">
                        <Users className="w-24 h-24 text-muted-foreground" />
                      </div>
                    )}
                  </figure>

                  {/* Ranking & Country */}
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="gap-1 text-lg py-2 px-4">
                      <Trophy className={`w-5 h-5 ${getMedalColor(profile.ranking)}`} />
                      #{profile.ranking} en {profile.location_country}
                    </Badge>
                  </div>

                  {/* Voting */}
                  <VoteButton
                    listingId={profile.id}
                    citySlug={profile.location_country.toLowerCase()}
                    activitySlug={profile.category}
                    currentVotes={0}
                  />
                </div>

                {/* Profile Info */}
                <div className="space-y-6">
                  <div>
                    <h1 className="text-4xl font-bold mb-2">{profile.full_name}</h1>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-5 h-5" />
                      <span className="text-lg">
                        {profile.location_city ? `${profile.location_city}, ` : ''}{profile.location_country}
                      </span>
                    </div>
                  </div>

                  {/* Bio */}
                  {profile.bio && (
                    <div>
                      <h2 className="text-xl font-semibold mb-2">Sobre mí</h2>
                      <p className="text-muted-foreground"><SpiderwebLink text={profile.bio || ''} /></p>
                    </div>
                  )}


                  {/* Unirse a Tribu */}
                  <div className="mt-4">
                    <UnirseATribuButton 
                      deporte={profile.specialty?.[0]}
                      ciudad={profile.location_city || undefined}
                      variant="outline"
                      size="lg"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Info Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Specialties */}
            {profile.specialty && profile.specialty.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-semibold">Especialidades</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.specialty.map((spec, idx) => (
                      <Badge key={idx} variant="secondary">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {profile.certifications && profile.certifications.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-semibold">Certificaciones</h3>
                  </div>
                  <ul className="space-y-2">
                    {profile.certifications.map((cert, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{cert}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Achievements */}
            {profile.achievements && profile.achievements.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-semibold">Logros</h3>
                  </div>
                  <ul className="space-y-2">
                    {profile.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Trophy className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Sport Background */}
            {profile.sport_background && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-semibold">Trayectoria Deportiva</h3>
                  </div>
                  <p className="text-muted-foreground">{profile.sport_background}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-primary rounded-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ¿Quieres destacar en {categoryName}?
            </h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Únete a nuestra plataforma y compite por estar entre los mejores de tu país
            </p>
            <Button size="lg" variant="secondary" asChild>
              <Link to="/sport-professional-register">
                Registrarte como Profesional
              </Link>
            </Button>
          </div>

          {/* Related Interlinks */}
          <RelatedInterlinks text={[profile.full_name, profile.bio, profile.category, profile.location_city, profile.location_country].filter(Boolean).join(' ')} maxLinks={4} />
        </div>
      </main>

      <Footer />
    </>
  );
};

export default SportProfessionalDetail;
